/**
 * faqSlice — Redux Toolkit slice for admin FAQ management (CON-CMS-003).
 *
 * Manages FAQ categories and their nested entries. Separated from cmsSlice
 * to keep the CMS page state (cmsSlice) and FAQ state independent — the FAQ
 * manager has its own full CRUD lifecycle with drag-drop reorder, which would
 * bloat cmsSlice significantly.
 *
 * State shape:
 *   categories: FaqCategoryDTO[] — full list with nested entries
 *   loading: Record<FaqLoadingKey, boolean>
 *   error: Record<FaqLoadingKey, string | null>
 *
 * Thunks:
 *   fetchFaqCategories — GET /api/v1/admin/cms/faq
 *   createFaqCategory  — POST /api/v1/admin/cms/faq/categories
 *   updateFaqCategory  — PUT /api/v1/admin/cms/faq/categories/:id
 *   deleteFaqCategory  — DELETE /api/v1/admin/cms/faq/categories/:id
 *   createFaqEntry     — POST /api/v1/admin/cms/faq/entries
 *   updateFaqEntry     — PUT /api/v1/admin/cms/faq/entries/:id
 *   deleteFaqEntry     — DELETE /api/v1/admin/cms/faq/entries/:id
 *   reorderFaqEntries  — PATCH /api/v1/admin/cms/faq/entries/reorder
 *
 * `reorderFaqEntries` uses optimistic local state: entries are reordered
 * immediately in the slice and reverted on rejection so the container can
 * show an error toast and restore the prior order.
 *
 * Per `.claude/rules/frontend-data-flow.md` §1, capsules NEVER import this
 * slice; only page-level containers do.
 */
import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { faqAdminApiClient } from '@dfq/shared/api/admin/faq';
import { isApiError } from '@dfq/shared/api/_envelope';
import type {
  FaqCategoryDTO,
  FaqEntryDTO,
  CreateFaqCategoryPayload,
  UpdateFaqCategoryPayload,
  CreateFaqEntryPayload,
  UpdateFaqEntryPayload,
  FaqEntryReorderItem,
  DeleteFaqCategoryPayload,
} from '@dfq/shared/types/cms';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export type FaqLoadingKey =
  | 'fetchCategories'
  | 'createCategory'
  | 'updateCategory'
  | 'deleteCategory'
  | 'createEntry'
  | 'updateEntry'
  | 'deleteEntry'
  | 'reorderEntries';

export interface FaqState {
  categories: FaqCategoryDTO[];
  loading: Record<FaqLoadingKey, boolean>;
  error: Record<FaqLoadingKey, string | null>;
}

const initialState: FaqState = {
  categories: [],
  loading: {
    fetchCategories: false,
    createCategory: false,
    updateCategory: false,
    deleteCategory: false,
    createEntry: false,
    updateEntry: false,
    deleteEntry: false,
    reorderEntries: false,
  },
  error: {
    fetchCategories: null,
    createCategory: null,
    updateCategory: null,
    deleteCategory: null,
    createEntry: null,
    updateEntry: null,
    deleteEntry: null,
    reorderEntries: null,
  },
};

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

export const fetchFaqCategories = createAsyncThunk(
  'faq/fetchCategories',
  async (_, { rejectWithValue }) => {
    try {
      return await faqAdminApiClient.fetchCategories();
    } catch (err) {
      if (isApiError(err)) return rejectWithValue(err.message);
      const msg = err instanceof Error ? err.message : 'Failed to fetch FAQ categories';
      return rejectWithValue(msg);
    }
  },
);

export const createFaqCategory = createAsyncThunk(
  'faq/createCategory',
  async (payload: CreateFaqCategoryPayload, { rejectWithValue }) => {
    try {
      return await faqAdminApiClient.createCategory(payload);
    } catch (err) {
      if (isApiError(err)) return rejectWithValue(err.message);
      const msg = err instanceof Error ? err.message : 'Failed to create category';
      return rejectWithValue(msg);
    }
  },
);

export const updateFaqCategory = createAsyncThunk(
  'faq/updateCategory',
  async (
    { id, ...payload }: { id: number } & UpdateFaqCategoryPayload,
    { rejectWithValue },
  ) => {
    try {
      return await faqAdminApiClient.updateCategory(id, payload);
    } catch (err) {
      if (isApiError(err)) return rejectWithValue(err.message);
      const msg = err instanceof Error ? err.message : 'Failed to update category';
      return rejectWithValue(msg);
    }
  },
);

export const deleteFaqCategory = createAsyncThunk(
  'faq/deleteCategory',
  async (
    { id, ...payload }: { id: number } & DeleteFaqCategoryPayload,
    { rejectWithValue },
  ) => {
    try {
      await faqAdminApiClient.deleteCategory(id, payload);
      return id;
    } catch (err) {
      if (isApiError(err)) return rejectWithValue(err.message);
      const msg = err instanceof Error ? err.message : 'Failed to delete category';
      return rejectWithValue(msg);
    }
  },
);

export const createFaqEntry = createAsyncThunk(
  'faq/createEntry',
  async (payload: CreateFaqEntryPayload, { rejectWithValue }) => {
    try {
      return await faqAdminApiClient.createEntry(payload);
    } catch (err) {
      if (isApiError(err)) return rejectWithValue(err.message);
      const msg = err instanceof Error ? err.message : 'Failed to create FAQ entry';
      return rejectWithValue(msg);
    }
  },
);

export const updateFaqEntry = createAsyncThunk(
  'faq/updateEntry',
  async (
    { id, ...payload }: { id: number } & UpdateFaqEntryPayload,
    { rejectWithValue },
  ) => {
    try {
      return await faqAdminApiClient.updateEntry(id, payload);
    } catch (err) {
      if (isApiError(err)) return rejectWithValue(err.message);
      const msg = err instanceof Error ? err.message : 'Failed to update FAQ entry';
      return rejectWithValue(msg);
    }
  },
);

export const deleteFaqEntry = createAsyncThunk(
  'faq/deleteEntry',
  async (id: number, { rejectWithValue }) => {
    try {
      await faqAdminApiClient.deleteEntry(id);
      return id;
    } catch (err) {
      if (isApiError(err)) return rejectWithValue(err.message);
      const msg = err instanceof Error ? err.message : 'Failed to delete FAQ entry';
      return rejectWithValue(msg);
    }
  },
);

export const reorderFaqEntries = createAsyncThunk(
  'faq/reorderEntries',
  async (
    { categoryId, entries }: { categoryId: number; entries: FaqEntryReorderItem[] },
    { rejectWithValue },
  ) => {
    try {
      await faqAdminApiClient.reorderEntries({ entries });
      return { categoryId, entries };
    } catch (err) {
      if (isApiError(err)) return rejectWithValue(err.message);
      const msg = err instanceof Error ? err.message : 'Failed to reorder entries';
      return rejectWithValue(msg);
    }
  },
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

export const faqSlice = createSlice({
  name: 'faq',
  initialState,
  reducers: {
    /** Optimistically update entry order within a category before the PATCH resolves. */
    optimisticReorderEntries(
      state,
      action: PayloadAction<{ categoryId: number; entries: FaqEntryDTO[] }>,
    ) {
      const cat = state.categories.find((c) => c.id === action.payload.categoryId);
      if (cat) {
        cat.entries = action.payload.entries;
      }
    },
    /** Revert entry order in a category after a failed PATCH. */
    revertReorderEntries(
      state,
      action: PayloadAction<{ categoryId: number; entries: FaqEntryDTO[] }>,
    ) {
      const cat = state.categories.find((c) => c.id === action.payload.categoryId);
      if (cat) {
        cat.entries = action.payload.entries;
      }
    },
    /** Optimistically toggle is_published on an entry before the PUT resolves. */
    optimisticTogglePublished(
      state,
      action: PayloadAction<{ entryId: number; isPublished: boolean }>,
    ) {
      for (const cat of state.categories) {
        const entry = cat.entries.find((e) => e.id === action.payload.entryId);
        if (entry) {
          entry.is_published = action.payload.isPublished;
          break;
        }
      }
    },
  },
  extraReducers: (builder) => {
    // ── fetchCategories ──────────────────────────────────────────────────────
    builder
      .addCase(fetchFaqCategories.pending, (state) => {
        state.loading.fetchCategories = true;
        state.error.fetchCategories = null;
      })
      .addCase(fetchFaqCategories.fulfilled, (state, action) => {
        state.loading.fetchCategories = false;
        state.categories = action.payload;
      })
      .addCase(fetchFaqCategories.rejected, (state, action) => {
        state.loading.fetchCategories = false;
        state.error.fetchCategories =
          (action.payload as string | undefined) ??
          action.error.message ??
          'Failed to fetch FAQ categories';
      });

    // ── createCategory ───────────────────────────────────────────────────────
    builder
      .addCase(createFaqCategory.pending, (state) => {
        state.loading.createCategory = true;
        state.error.createCategory = null;
      })
      .addCase(createFaqCategory.fulfilled, (state, action) => {
        state.loading.createCategory = false;
        state.categories.push(action.payload);
      })
      .addCase(createFaqCategory.rejected, (state, action) => {
        state.loading.createCategory = false;
        state.error.createCategory =
          (action.payload as string | undefined) ??
          action.error.message ??
          'Failed to create category';
      });

    // ── updateCategory ───────────────────────────────────────────────────────
    builder
      .addCase(updateFaqCategory.pending, (state) => {
        state.loading.updateCategory = true;
        state.error.updateCategory = null;
      })
      .addCase(updateFaqCategory.fulfilled, (state, action) => {
        state.loading.updateCategory = false;
        const idx = state.categories.findIndex((c) => c.id === action.payload.id);
        if (idx !== -1) {
          // Preserve current entries since the update response may not include them.
          state.categories[idx] = {
            ...action.payload,
            entries: state.categories[idx]!.entries,
          };
        }
      })
      .addCase(updateFaqCategory.rejected, (state, action) => {
        state.loading.updateCategory = false;
        state.error.updateCategory =
          (action.payload as string | undefined) ??
          action.error.message ??
          'Failed to update category';
      });

    // ── deleteCategory ───────────────────────────────────────────────────────
    builder
      .addCase(deleteFaqCategory.pending, (state) => {
        state.loading.deleteCategory = true;
        state.error.deleteCategory = null;
      })
      .addCase(deleteFaqCategory.fulfilled, (state, action) => {
        state.loading.deleteCategory = false;
        state.categories = state.categories.filter((c) => c.id !== action.payload);
      })
      .addCase(deleteFaqCategory.rejected, (state, action) => {
        state.loading.deleteCategory = false;
        state.error.deleteCategory =
          (action.payload as string | undefined) ??
          action.error.message ??
          'Failed to delete category';
      });

    // ── createEntry ──────────────────────────────────────────────────────────
    builder
      .addCase(createFaqEntry.pending, (state) => {
        state.loading.createEntry = true;
        state.error.createEntry = null;
      })
      .addCase(createFaqEntry.fulfilled, (state, action) => {
        state.loading.createEntry = false;
        const cat = state.categories.find(
          (c) => c.id === action.payload.faq_category_id,
        );
        if (cat) {
          cat.entries.push(action.payload);
        }
      })
      .addCase(createFaqEntry.rejected, (state, action) => {
        state.loading.createEntry = false;
        state.error.createEntry =
          (action.payload as string | undefined) ??
          action.error.message ??
          'Failed to create FAQ entry';
      });

    // ── updateEntry ──────────────────────────────────────────────────────────
    builder
      .addCase(updateFaqEntry.pending, (state) => {
        state.loading.updateEntry = true;
        state.error.updateEntry = null;
      })
      .addCase(updateFaqEntry.fulfilled, (state, action) => {
        state.loading.updateEntry = false;
        for (const cat of state.categories) {
          const idx = cat.entries.findIndex((e) => e.id === action.payload.id);
          if (idx !== -1) {
            cat.entries[idx] = action.payload;
            break;
          }
        }
      })
      .addCase(updateFaqEntry.rejected, (state, action) => {
        state.loading.updateEntry = false;
        state.error.updateEntry =
          (action.payload as string | undefined) ??
          action.error.message ??
          'Failed to update FAQ entry';
      });

    // ── deleteEntry ──────────────────────────────────────────────────────────
    builder
      .addCase(deleteFaqEntry.pending, (state) => {
        state.loading.deleteEntry = true;
        state.error.deleteEntry = null;
      })
      .addCase(deleteFaqEntry.fulfilled, (state, action) => {
        state.loading.deleteEntry = false;
        for (const cat of state.categories) {
          const before = cat.entries.length;
          cat.entries = cat.entries.filter((e) => e.id !== action.payload);
          if (cat.entries.length < before) break;
        }
      })
      .addCase(deleteFaqEntry.rejected, (state, action) => {
        state.loading.deleteEntry = false;
        state.error.deleteEntry =
          (action.payload as string | undefined) ??
          action.error.message ??
          'Failed to delete FAQ entry';
      });

    // ── reorderEntries ───────────────────────────────────────────────────────
    builder
      .addCase(reorderFaqEntries.pending, (state) => {
        state.loading.reorderEntries = true;
        state.error.reorderEntries = null;
      })
      .addCase(reorderFaqEntries.fulfilled, (state) => {
        state.loading.reorderEntries = false;
      })
      .addCase(reorderFaqEntries.rejected, (state, action) => {
        state.loading.reorderEntries = false;
        state.error.reorderEntries =
          (action.payload as string | undefined) ??
          action.error.message ??
          'Failed to reorder entries';
      });
  },
});

export const {
  optimisticReorderEntries,
  revertReorderEntries,
  optimisticTogglePublished,
} = faqSlice.actions;

export const faqReducer = faqSlice.reducer;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export interface FaqRootState {
  faq: FaqState;
}

export const selectFaqCategories = (state: FaqRootState): FaqCategoryDTO[] =>
  state.faq.categories;

export const selectFaqLoading = (
  state: FaqRootState,
): Record<FaqLoadingKey, boolean> => state.faq.loading;

export const selectFaqError = (
  state: FaqRootState,
): Record<FaqLoadingKey, string | null> => state.faq.error;
